import { useState, useEffect, useCallback, type ReactNode } from 'react';
import {
  platformApi, platformTokens, PlatformApiError,
  startImpersonation, launchImpersonation,
  type PlatformAdminView, type TenantSummary,
} from '../lib/platformApi';
import { PipelinePanel } from './PipelinePanel';
import WhatsAppOnboardingPanel from './WhatsAppOnboardingPanel';

export function PlatformApp() {
  const [me, setMe] = useState<PlatformAdminView | null>(null);
  const [booting, setBooting] = useState(true);

  useEffect(() => {
    if (!platformTokens.access()) { setBooting(false); return; }
    platformApi.get<PlatformAdminView>('/api/platform/auth/me')
      .then(setMe).catch(() => platformTokens.clear()).finally(() => setBooting(false));
  }, []);

  if (booting) return <Shell><p style={{ color: 'var(--ink-3)' }}>Loading…</p></Shell>;
  if (!me) return <Login onIn={setMe} />;
  return <Console me={me} onOut={() => { platformTokens.clear(); setMe(null); }} />;
}

// ---------------- Login ----------------
function Login({ onIn }: { onIn: (a: PlatformAdminView) => void }) {
  const [stage, setStage] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestOtp = async () => {
    setBusy(true); setError(null);
    try { await platformApi.requestOtp(email.trim()); setStage('code'); }
    catch { setError('Could not send the code. Try again.'); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true); setError(null);
    try {
      const res = await platformApi.verifyOtp(email.trim(), code.trim());
      platformTokens.save(res);
      onIn(res.admin);
    } catch (e) { setError(e instanceof PlatformApiError && e.status === 401 ? 'Invalid or expired code.' : 'Sign-in failed.'); }
    finally { setBusy(false); }
  };

  return (
    <Shell>
      <div style={{ maxWidth: 380, margin: '8vh auto 0' }}>
        <div className="eyebrow" style={{ marginBottom: 8 }}>Eduvora · Platform</div>
        <h1 className="h-display" style={{ fontSize: 26, marginBottom: 4 }}>Super Admin</h1>
        <p style={{ color: 'var(--ink-3)', fontSize: 14, marginBottom: 24 }}>
          {stage === 'email' ? 'Sign in with your platform email.' : `Enter the code sent to ${email}.`}
        </p>
        {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', marginBottom: 14, justifyContent: 'flex-start' }}>{error}</div>}

        {stage === 'email' ? (
          <>
            <label className="label">Email</label>
            <input className="input" type="email" value={email} autoFocus
              onChange={(e) => setEmail(e.target.value)} placeholder="admin@eduvoratech.com"
              onKeyDown={(e) => e.key === 'Enter' && email.trim() && requestOtp()} style={{ marginBottom: 16 }} />
            <button className="btn btn-accent" style={{ width: '100%', justifyContent: 'center' }}
              disabled={!email.trim() || busy} onClick={requestOtp}>{busy ? 'Sending…' : 'Send code'}</button>
          </>
        ) : (
          <>
            <label className="label">Code</label>
            <input className="input mono" value={code} autoFocus
              onChange={(e) => setCode(e.target.value)} placeholder="123456"
              onKeyDown={(e) => e.key === 'Enter' && code.trim() && verify()} style={{ marginBottom: 16, letterSpacing: '.2em', textAlign: 'center' }} />
            <button className="btn btn-accent" style={{ width: '100%', justifyContent: 'center' }}
              disabled={!code.trim() || busy} onClick={verify}>{busy ? 'Verifying…' : 'Sign in'}</button>
            <button className="btn" style={{ width: '100%', justifyContent: 'center', marginTop: 8, background: 'transparent', border: 'none', color: 'var(--ink-3)' }}
              onClick={() => { setStage('email'); setCode(''); setError(null); }}>Use a different email</button>
          </>
        )}
        <p style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 20 }}>
          In local dev the code is printed to the API console.
        </p>
      </div>
    </Shell>
  );
}

// ---------------- Console ----------------
function Console({ me, onOut }: { me: PlatformAdminView; onOut: () => void }) {
  /**
   * ⚠ THE SERVER IS WHAT ENFORCES THIS. Every route behind Schools and Platform admins is gated by
   * the `platform_owner` policy; a Sales operator who reconstructs the URL gets a 403, not data.
   * What follows is only so the console shows a seller the one thing they can actually use, rather
   * than two tabs that answer every click with an error.
   */
  const isOwner = me.role === 'Owner';
  const [tab, setTab] = useState<'tenants' | 'admins' | 'pipeline' | 'whatsapp'>(isOwner ? 'tenants' : 'pipeline');
  return (
    <Shell>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24, gap: 12 }}>
        <div>
          <div className="eyebrow">Eduvora · Platform</div>
          <h1 className="h-display" style={{ fontSize: 24 }}>Super Admin Console</h1>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>{me.displayName}</span>
          {/* ⚠ Shown always, including for an Owner. "Which powers am I signed in with?" should be
              answerable by looking, not by clicking something and seeing whether it works. */}
          <span className="chip" style={{ fontSize: 11 }}>{me.role}</span>
          <button className="btn" style={{ padding: '5px 12px', fontSize: 13 }} onClick={onOut}>Sign out</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        {isOwner && <TabBtn active={tab === 'tenants'} onClick={() => setTab('tenants')}>Schools</TabBtn>}
        {isOwner && <TabBtn active={tab === 'admins'} onClick={() => setTab('admins')}>Platform admins</TabBtn>}
        {/* ⚠ Behind its own PIN — see PipelinePanel. The tab is visible to any platform admin; what
            is behind it is not. Hiding the tab itself would only mean somebody wonders what the
            gap is for. */}
        <TabBtn active={tab === 'pipeline'} onClick={() => setTab('pipeline')}>Pipeline</TabBtn>
        {/* ⚠ VISIBLE TO EVERY PLATFORM ADMIN, NOT JUST THE OWNER. The whole point is that somebody
            other than one person can onboard a school without repeating the first attempt — gating
            it to Owner would put the knowledge back in one head, which is the problem it fixes. */}
        <TabBtn active={tab === 'whatsapp'} onClick={() => setTab('whatsapp')}>WhatsApp</TabBtn>
      </div>

      {isOwner && tab === 'tenants' && <Tenants />}
      {isOwner && tab === 'admins' && <Admins meId={me.id} />}
      {tab === 'pipeline' && <PipelinePanel />}
      {tab === 'whatsapp' && <WhatsAppOnboardingPanel />}
    </Shell>
  );
}

// ---------------- Tenants ----------------
function Tenants() {
  const [rows, setRows] = useState<TenantSummary[]>([]);
  const [creating, setCreating] = useState(false);
  const [entering, setEntering] = useState<TenantSummary | null>(null);
  const [features, setFeatures] = useState<TenantSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await platformApi.get<TenantSummary[]>('/api/platform/tenants')); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (t: TenantSummary) => {
    const action = t.isActive ? 'suspend' : 'reactivate';
    await platformApi.post(`/api/platform/tenants/${t.id}/${action}`);
    load();
  };

  return (
    <div>
      <div style={{ display: 'flex', marginBottom: 14 }}>
        <div style={{ color: 'var(--ink-3)', fontSize: 13, alignSelf: 'center' }}>
          {rows.length} school{rows.length === 1 ? '' : 's'}
        </div>
        <button className="btn btn-accent" style={{ marginLeft: 'auto' }} onClick={() => setCreating(true)}>+ New school</button>
      </div>

      {loading ? <p style={{ color: 'var(--ink-3)' }}>Loading…</p> : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: '1px solid var(--line)', textAlign: 'left' }}>
              <Th>School</Th><Th>Slug</Th><Th align="right">Users</Th><Th align="right">Students</Th><Th>Status</Th><Th></Th>
            </tr></thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} style={{ borderBottom: '1px solid var(--line-2)' }}>
                  <Td><strong>{t.name}</strong></Td>
                  <Td><span className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>{t.slug}</span></Td>
                  <Td align="right"><span className="mono">{t.userCount}</span></Td>
                  <Td align="right"><span className="mono">{t.studentCount}</span></Td>
                  <Td><span className={`pill ${t.isActive ? 'pill-ok' : 'pill-danger'}`} style={{ fontSize: 11 }}>{t.isActive ? 'Active' : 'Suspended'}</span></Td>
                  <Td align="right">
                    <div style={{ display: 'inline-flex', gap: 6 }}>
                      {t.isActive && (
                        <button className="btn" style={{ padding: '4px 12px', fontSize: 13 }} onClick={() => setEntering(t)} title="Read-only support access">
                          Enter
                        </button>
                      )}
                      <button className="btn" style={{ padding: '4px 12px', fontSize: 13 }} onClick={() => setFeatures(t)} title="Turn modules on/off for this school">
                        Features
                      </button>
                      <button className="btn" style={{ padding: '4px 12px', fontSize: 13 }} onClick={() => toggle(t)}>
                        {t.isActive ? 'Suspend' : 'Reactivate'}
                      </button>
                    </div>
                  </Td>
                </tr>
              ))}
              {rows.length === 0 && <tr><Td colSpan={6} muted>No schools yet. Create the first one.</Td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {creating && <CreateTenant onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} />}
      {entering && <EnterSchool tenant={entering} onClose={() => setEntering(null)} />}
      {features && <TenantFeatures tenant={features} onClose={() => setFeatures(null)} />}
    </div>
  );
}

function EnterSchool({ tenant, onClose }: { tenant: TenantSummary; onClose: () => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const enter = async () => {
    if (!reason.trim()) return;
    setBusy(true); setError(null);
    try {
      const started = await startImpersonation(tenant.id, reason.trim());
      launchImpersonation(started); // navigates away into the tenant app (read-only)
    } catch (e) {
      setError(e instanceof PlatformApiError ? e.message : 'Could not start support access.');
      setBusy(false);
    }
  };

  return (
    <Overlay onClose={onClose}>
      <div className="eyebrow" style={{ marginBottom: 6 }}>Support access</div>
      <h2 className="h-display" style={{ fontSize: 20, marginBottom: 6 }}>Enter {tenant.name}</h2>
      <div className="pill pill-warn" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', marginBottom: 14, justifyContent: 'flex-start', fontSize: 12 }}>
        Read-only. You’ll see this school’s data to diagnose, but can’t change anything. The
        school is notified you accessed it. Session ends automatically after 15 minutes.
      </div>
      {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}
      <label className="label">Reason (required — recorded in the audit)</label>
      <input className="input" autoFocus value={reason} onChange={(e) => setReason(e.target.value)}
        placeholder="Investigating ticket #123 — fees not showing"
        onKeyDown={(e) => e.key === 'Enter' && reason.trim() && enter()} style={{ marginBottom: 20 }} />
      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn" style={{ flex: 1, justifyContent: 'center' }} onClick={onClose}>Cancel</button>
        <button className="btn btn-accent" style={{ flex: 1, justifyContent: 'center' }} disabled={!reason.trim() || busy} onClick={enter}>
          {busy ? 'Entering…' : 'Enter (read-only)'}
        </button>
      </div>
    </Overlay>
  );
}

interface ModuleInfo { key: string; label: string; enabled: boolean; }
interface FeaturesResp { modules: ModuleInfo[]; }

function TenantFeatures({ tenant, onClose }: { tenant: TenantSummary; onClose: () => void }) {
  const [modules, setModules] = useState<ModuleInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    platformApi.get<FeaturesResp>(`/api/platform/tenants/${tenant.id}/features`)
      .then((r) => setModules(r.modules))
      .catch(() => setModules([]))
      .finally(() => setLoading(false));
  }, [tenant.id]);

  const toggle = (key: string) =>
    setModules((ms) => ms.map((m) => (m.key === key ? { ...m, enabled: !m.enabled } : m)));

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const enabledModules = modules.filter((m) => m.enabled).map((m) => m.key);
      const r = await platformApi.put<FeaturesResp>(`/api/platform/tenants/${tenant.id}/features`, { enabledModules });
      setModules(r.modules);
      setMsg('Saved. The school sees the change next time they load the app.');
    } catch {
      setMsg('Could not save.');
    } finally { setBusy(false); }
  };

  return (
    <Overlay onClose={onClose}>
      <div className="eyebrow" style={{ marginBottom: 6 }}>Features</div>
      <h2 className="h-display" style={{ fontSize: 20, marginBottom: 4 }}>{tenant.name}</h2>
      <p style={{ color: 'var(--ink-3)', fontSize: 12, marginBottom: 16 }}>
        Turn optional modules on or off for this school. Core modules (students, fees, admissions,
        attendance) are always on and aren't listed.
      </p>

      {loading ? <p style={{ color: 'var(--ink-3)' }}>Loading…</p> : (
        <div style={{ display: 'grid', gap: 8, marginBottom: 16 }}>
          {modules.map((m) => (
            <label key={m.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid var(--line)', borderRadius: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={m.enabled} onChange={() => toggle(m.key)} />
              <span style={{ fontWeight: 500 }}>{m.label}</span>
              <span style={{ marginLeft: 'auto', fontSize: 11, color: m.enabled ? 'var(--ok, #2e7d32)' : 'var(--ink-3)' }}>
                {m.enabled ? 'On' : 'Off'}
              </span>
            </label>
          ))}
        </div>
      )}

      {msg && <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 12 }}>{msg}</div>}

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-accent" style={{ flex: 1, justifyContent: 'center' }} disabled={busy || loading} onClick={save}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button className="btn" onClick={onClose}>Close</button>
      </div>
    </Overlay>
  );
}

function CreateTenant({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [slug, setSlug] = useState(''); const [name, setName] = useState('');
  const [primary, setPrimary] = useState('#0E1A2B'); const [accent, setAccent] = useState('#C9A15A');
  const [email, setEmail] = useState('');
  const [adminEmail, setAdminEmail] = useState(''); const [adminName, setAdminName] = useState('');
  const [busy, setBusy] = useState(false); const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      await platformApi.post('/api/platform/tenants', {
        slug: slug.trim().toLowerCase(), name: name.trim(),
        primaryColor: primary, accentColor: accent, supportEmail: email.trim() || null,
        adminEmail: adminEmail.trim().toLowerCase() || null, adminName: adminName.trim() || null,
      });
      onDone();
    } catch (e) { setError(e instanceof PlatformApiError ? e.message : 'Could not create the school.'); }
    finally { setBusy(false); }
  };

  return (
    <Overlay onClose={onClose}>
      <h2 className="h-display" style={{ fontSize: 20, marginBottom: 16 }}>New school</h2>
      {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}
      <label className="label">Slug (subdomain)</label>
      <input className="input mono" autoFocus value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="stmarys" style={{ marginBottom: 4 }} />
      <p style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 12 }}>{slug.trim() ? `${slug.trim().toLowerCase()}.eduvoratech.com` : 'used as the school’s subdomain'}</p>
      <label className="label">Name</label>
      <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="St. Mary’s School" style={{ marginBottom: 12 }} />
      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <label className="label">Brand color</label>
          <input className="input mono" value={primary} onChange={(e) => setPrimary(e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <label className="label">Accent color</label>
          <input className="input mono" value={accent} onChange={(e) => setAccent(e.target.value)} />
        </div>
      </div>
      <label className="label">Support email</label>
      <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="office@stmarys.edu" style={{ marginBottom: 12 }} />
      <div style={{ height: 1, background: 'var(--line)', margin: '4px 0 14px' }} />
      <label className="label">School admin email</label>
      <input className="input" type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} placeholder="principal@stmarys.edu" style={{ marginBottom: 4 }} />
      <p style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 12 }}>Provisioned as this school’s first admin (TrustAdmin). They sign in with this email and can invite the rest of the staff.</p>
      <label className="label">Admin name <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>(optional)</span></label>
      <input className="input" value={adminName} onChange={(e) => setAdminName(e.target.value)} placeholder="Dr. Jane Rao" style={{ marginBottom: 20 }} />
      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn" style={{ flex: 1, justifyContent: 'center' }} onClick={onClose}>Cancel</button>
        <button className="btn btn-accent" style={{ flex: 1, justifyContent: 'center' }} disabled={!slug.trim() || !name.trim() || busy} onClick={submit}>
          {busy ? 'Creating…' : 'Create school'}
        </button>
      </div>
    </Overlay>
  );
}

// ---------------- Admins ----------------
function Admins({ meId }: { meId: string }) {
  const [rows, setRows] = useState<PlatformAdminView[]>([]);
  const [inviting, setInviting] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await platformApi.get<PlatformAdminView[]>('/api/platform/admins')); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (a: PlatformAdminView) => {
    const action = a.isActive ? 'deactivate' : 'reactivate';
    await platformApi.post(`/api/platform/admins/${a.id}/${action}`);
    load();
  };

  return (
    <div>
      <div style={{ display: 'flex', marginBottom: 14 }}>
        <div style={{ color: 'var(--ink-3)', fontSize: 13, alignSelf: 'center' }}>{rows.length} admin{rows.length === 1 ? '' : 's'}</div>
        <button className="btn btn-accent" style={{ marginLeft: 'auto' }} onClick={() => setInviting(true)}>+ Invite admin</button>
      </div>

      {loading ? <p style={{ color: 'var(--ink-3)' }}>Loading…</p> : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: '1px solid var(--line)', textAlign: 'left' }}>
              <Th>Name</Th><Th>Email</Th><Th>Last login</Th><Th>Status</Th><Th></Th>
            </tr></thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} style={{ borderBottom: '1px solid var(--line-2)' }}>
                  <Td><strong>{a.displayName}</strong>{a.id === meId && <span className="pill pill-muted" style={{ fontSize: 10, marginLeft: 6 }}>you</span>}</Td>
                  <Td><span className="mono" style={{ fontSize: 12 }}>{a.email}</span></Td>
                  <Td><span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{a.lastLoginAt ?? '—'}</span></Td>
                  <Td><span className={`pill ${a.isActive ? 'pill-ok' : 'pill-danger'}`} style={{ fontSize: 11 }}>{a.isActive ? 'Active' : 'Disabled'}</span></Td>
                  <Td align="right">
                    {a.id !== meId && (
                      <button className="btn" style={{ padding: '4px 12px', fontSize: 13 }} onClick={() => toggle(a)}>
                        {a.isActive ? 'Disable' : 'Enable'}
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {inviting && <InviteAdmin onClose={() => setInviting(false)} onDone={() => { setInviting(false); load(); }} />}
    </div>
  );
}

function InviteAdmin({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [email, setEmail] = useState(''); const [name, setName] = useState('');
  /**
   * ⚠ STARTS ON Sales, AND THE SERVER REJECTS AN ABSENT ROLE OUTRIGHT. The overwhelmingly common
   * invite is a seller, and the rarest one hands over impersonation across every school. The
   * default should be the one you can undo.
   */
  const [role, setRole] = useState<'Sales' | 'Owner'>('Sales');
  const [busy, setBusy] = useState(false); const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      await platformApi.post('/api/platform/admins', { email: email.trim().toLowerCase(), displayName: name.trim(), role });
      onDone();
    } catch (e) { setError(e instanceof PlatformApiError ? e.message : 'Could not invite the admin.'); }
    finally { setBusy(false); }
  };

  return (
    <Overlay onClose={onClose}>
      <h2 className="h-display" style={{ fontSize: 20, marginBottom: 16 }}>Invite platform admin</h2>
      {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}
      <label className="label">Email</label>
      <input className="input" type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ops@eduvoratech.com" style={{ marginBottom: 12 }} />
      <label className="label">Name</label>
      <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" style={{ marginBottom: 12 }} />
      <label className="label">Role</label>
      <select className="input" value={role} onChange={(e) => setRole(e.target.value as 'Sales' | 'Owner')} style={{ marginBottom: 8 }}>
        <option value="Sales">Sales — pipeline and prospects only</option>
        <option value="Owner">Owner — everything, including signing in as any school</option>
      </select>
      {/* ⚠ Spelled out rather than left to the word "Owner". The cost of this choice is invisible
          at the moment it is made and obvious six months later. */}
      <p style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 20, lineHeight: 1.5 }}>
        {role === 'Sales'
          ? 'They see the pipeline, prospects and market intelligence. They cannot open any school’s data, suspend a school, or sign in as a user.'
          : 'An Owner can sign in as any headmaster at any school and read every student record on the platform. Give this only to someone you would trust with the database.'}
      </p>
      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn" style={{ flex: 1, justifyContent: 'center' }} onClick={onClose}>Cancel</button>
        <button className="btn btn-accent" style={{ flex: 1, justifyContent: 'center' }} disabled={!email.trim() || !name.trim() || busy} onClick={submit}>
          {busy ? 'Inviting…' : 'Send invite'}
        </button>
      </div>
    </Overlay>
  );
}

// ---------------- shared bits ----------------
function Shell({ children }: { children: ReactNode }) {
  return <div style={{ minHeight: '100vh', background: 'var(--bg)', padding: '32px 28px', maxWidth: 1000, margin: '0 auto' }}>{children}</div>;
}
function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button className="btn" onClick={onClick} style={{
      padding: '7px 14px', background: active ? 'var(--brand)' : 'var(--surface)',
      color: active ? '#fff' : 'var(--ink-2)', borderColor: active ? 'var(--brand)' : 'var(--line)',
    }}>{children}</button>
  );
}
function Overlay({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,12,18,.45)', zIndex: 50, display: 'grid', placeItems: 'center', padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ width: 440, maxWidth: '100%', padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>{children}</div>
    </div>
  );
}
function Th({ children, align }: { children?: ReactNode; align?: 'right' }) {
  return <th style={{ padding: '10px 14px', fontSize: 11, fontWeight: 600, color: 'var(--ink-2)', textTransform: 'uppercase', letterSpacing: '.02em', textAlign: align ?? 'left' }}>{children}</th>;
}
function Td({ children, colSpan, muted, align }: { children?: ReactNode; colSpan?: number; muted?: boolean; align?: 'right' }) {
  return <td colSpan={colSpan} style={{ padding: '10px 14px', color: muted ? 'var(--ink-3)' : 'var(--ink)', textAlign: muted ? 'center' : (align ?? 'left'), fontSize: 13 }}>{children}</td>;
}
